import { apiClient } from '@/api/client'
import {
  companyPayrollRunsPath,
  companyPayrollRunPath,
  companyPayrollRunPreflightPath,
  companyPayrollRunCalculatePath,
  companyPayrollRunSubmitPath,
  companyPayrollRunApprovePath,
  companyPayrollRunRejectPath,
  companyPayrollRunVoidPath,
  companyPayrollRunAdjustPath,
  companyPayrollRunItemsPath,
  companyPayrollRunItemPath,
  companyPayrollRunPreviewPath,
  companyPayrollSummaryPath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { PaginatedResponse } from '@/types/common.types'
import type {
  PayrollRun,
  PayrollItem,
  PreflightResult,
  CompanyPayrollSummary,
  CreatePayrollRunDto,
  CreatePayrollAdjustmentDto,
  PayrollRunListFilters,
} from '@/features/payroll/types/payroll.types'

export async function listRuns(
  companyId: string,
  params?: PayrollRunListFilters,
): Promise<PaginatedResponse<PayrollRun>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedResponse<PayrollRun>>>(
    companyPayrollRunsPath(companyId),
    { params },
  )
  return data.data
}

export async function getRun(companyId: string, runId: string): Promise<PayrollRun> {
  const { data } = await apiClient.get<ApiResponseWrapper<PayrollRun>>(
    companyPayrollRunPath(companyId, runId),
  )
  return data.data
}

export async function preflightRun(
  companyId: string,
  runId: string,
): Promise<PreflightResult> {
  const { data } = await apiClient.get<ApiResponseWrapper<PreflightResult>>(
    companyPayrollRunPreflightPath(companyId, runId),
  )
  return data.data
}

export async function createRun(
  companyId: string,
  body: CreatePayrollRunDto,
): Promise<PayrollRun> {
  const { data } = await apiClient.post<ApiResponseWrapper<PayrollRun>>(
    companyPayrollRunsPath(companyId),
    body,
  )
  return data.data
}

export async function calculateRun(companyId: string, runId: string): Promise<PayrollRun> {
  const { data } = await apiClient.post<ApiResponseWrapper<PayrollRun>>(
    companyPayrollRunCalculatePath(companyId, runId),
  )
  return data.data
}

export async function submitRun(companyId: string, runId: string): Promise<PayrollRun> {
  const { data } = await apiClient.post<ApiResponseWrapper<PayrollRun>>(
    companyPayrollRunSubmitPath(companyId, runId),
  )
  return data.data
}

export async function approveRun(companyId: string, runId: string): Promise<PayrollRun> {
  const { data } = await apiClient.post<ApiResponseWrapper<PayrollRun>>(
    companyPayrollRunApprovePath(companyId, runId),
  )
  return data.data
}

export async function rejectRun(
  companyId: string,
  runId: string,
  reason: string,
): Promise<PayrollRun> {
  const { data } = await apiClient.post<ApiResponseWrapper<PayrollRun>>(
    companyPayrollRunRejectPath(companyId, runId),
    { reason },
  )
  return data.data
}

export async function voidRun(companyId: string, runId: string): Promise<PayrollRun> {
  const { data } = await apiClient.post<ApiResponseWrapper<PayrollRun>>(
    companyPayrollRunVoidPath(companyId, runId),
  )
  return data.data
}

export async function createAdjustment(
  companyId: string,
  runId: string,
  body: CreatePayrollAdjustmentDto,
): Promise<PayrollRun> {
  const { data } = await apiClient.post<ApiResponseWrapper<PayrollRun>>(
    companyPayrollRunAdjustPath(companyId, runId),
    body,
  )
  return data.data
}

export async function listRunItems(
  companyId: string,
  runId: string,
  params?: { page?: number; limit?: number },
): Promise<PaginatedResponse<PayrollItem>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedResponse<PayrollItem>>>(
    companyPayrollRunItemsPath(companyId, runId),
    { params },
  )
  return data.data
}

export async function getRunItem(
  companyId: string,
  runId: string,
  itemId: string,
): Promise<PayrollItem> {
  const { data } = await apiClient.get<ApiResponseWrapper<PayrollItem>>(
    companyPayrollRunItemPath(companyId, runId, itemId),
  )
  return data.data
}

export async function downloadRunPreview(
  companyId: string,
  runId: string,
  format: 'pdf' | 'csv',
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    companyPayrollRunPreviewPath(companyId, runId),
    { params: { format }, responseType: 'blob' },
  )
  return data
}

export async function getCompanyPayrollSummary(
  companyId: string,
  year: number,
  month: number,
): Promise<CompanyPayrollSummary> {
  const { data } = await apiClient.get<ApiResponseWrapper<CompanyPayrollSummary>>(
    companyPayrollSummaryPath(companyId),
    { params: { year, month } },
  )
  return data.data
}
